import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  MenuItem,
  Autocomplete,
  Chip,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchData, postQuery } from "../../services/BackendServices";
import { uploadVideoToS3 } from "../../pages/Requirement/services/RequirementServices";
import { useLoading } from "../../context/LoadingContext";

export default function ProfileStepper() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setLoading } = useLoading();

  // Access userData from navigation state
  const userData = location.state?.userData || {};

  // Define steps based on role
  const steps =
    userData.role === "seller-farmer"
      ? ["Profile"]
      : userData.role === "buyer"
      ? ["Profile", "KYC"]
      : ["Profile", "Products", "KYC"];
  console.log("userData in ProfileStepper:", userData);
  console.log("Available userData fields:", Object.keys(userData));

  // Check for common OAuth fields
  if (userData.email) console.log("Email:", userData.email);
  if (userData.name) console.log("Name:", userData.name);
  if (userData.given_name) console.log("Given name:", userData.given_name);
  if (userData.family_name) console.log("Family name:", userData.family_name);
  if (userData.picture) console.log("Picture URL:", userData.picture);

  // State for dynamic data
  const [countryMaster, setCountryMaster] = useState([]);
  const [productList, setProductList] = useState([]);
  const [productGradeList, setProductGradeList] = useState([]);
  const [activeStep, setActiveStep] = useState(0);

  // Validation state
  const [errors, setErrors] = useState({});
  const [uploadedFile, setUploadedFile] = useState(null);

  // Load countries from API
  const loadCountries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchData({
        target: "listDropDownOptions",
        userOrder: "Value",
        rowStart: 0,
        pageSize: 1000,
        filterTerms: { Type: "Country" },
      });

      if (response && response.items.length > 0) {
        setCountryMaster(response.items);
      }
    } catch (error) {
      console.error("Error loading countries:", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  // Load countries on component mount
  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  // Load products for dropdowns
  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchData({
        target: "listProducts",
        userOrder: "ProductName",
        rowStart: 0,
        pageSize: 500,
        filterTerms: { Status: "ACTIVE" },
      });

      if (response && response.items.length > 0) {
        setProductList(response.items);
      } else {
        setProductList([]);
      }
    } catch (error) {
      console.error("Error loading products:", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  // Load product grades based on product name
  const loadProductGrades = useCallback(
    async (productName) => {
      setLoading(true);
      try {
        const [allGrades] = await Promise.all([
          fetchData({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: 500,
            filterTerms: { ProductName: productName, Status: "ACTIVE" },
          }),
        ]);
        if (allGrades.items.length > 0) {
          setProductGradeList(allGrades.items);
        } else {
          setProductGradeList([]);
        }
      } catch (error) {
        console.error("Error loading product grades: ", error);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  // Load products on component mount
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);
  const [formData, setFormData] = useState({
    fullName:
      userData.name || userData.given_name || userData.family_name
        ? `${userData.given_name || ""} ${userData.family_name || ""}`.trim()
        : userData.fullName || "",
    address: userData.address || "",
    country: userData.country || "US",
    phoneNumber: userData.phone_number || userData.phoneNumber || "",
    productsToSell: userData.productsToSell || [],
    productsToBuy: userData.productsToBuy || [],
    documentName: userData.documentName || "",
    documentIdNumber: userData.documentIdNumber || "",
    uploadIdPhoto: userData.uploadIdPhoto || null,
    email: userData.email || "",
    Id: userData.Id || "",
  });

  // Validation functions
  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    if (!formData.country) {
      newErrors.country = "Country is required";
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};

    if (!formData.documentName.trim()) {
      newErrors.documentName = "Document name is required";
    }

    if (!formData.documentIdNumber.trim()) {
      newErrors.documentIdNumber = "Document ID number is required";
    }

    if (!uploadedFile?.fileName && !formData.uploadIdPhoto) {
      newErrors.uploadedDocument = "Uploaded document is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // S3 Upload function
  const handleFileUpload = async (file) => {
    try {
      setLoading(true);
      const result = await uploadVideoToS3(file);
      setUploadedFile({
        fileName: result.fileName,
        url: result.url,
      });
      return result;
    } catch (error) {
      console.error("Error uploading file to S3:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    // Clear previous errors
    setErrors({});

    // Validate Step 1 (Profile)
    if (activeStep === 0) {
      if (!validateStep1()) {
        return;
      }
    }

    // For seller-farmer: only Profile step, no next - use handleNextFinish instead
    // For buyer: step 0 (Profile) -> step 1 (KYC)
    // For global-partner: step 0 (Profile) -> step 1 (Products) -> step 2 (KYC)

    // Step 2 (Products) - no validation needed as requested (only for global-partner)

    // Validate KYC step when it's the last step (not for seller-farmer)
    if (userData.role !== "seller-farmer") {
      const isKYCStep =
        userData.role === "buyer" ? activeStep === 1 : activeStep === 2;
      if (isKYCStep) {
        if (!validateStep3()) {
          return;
        }
      }
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleNextFinish = async () => {
    // Validate final step - skip KYC validation for seller-farmer
    if (userData.role !== "seller-farmer") {
      if (!validateStep3()) {
        return;
      }
    } else {
      // For seller-farmer, validate profile step
      if (!validateStep1()) {
        return;
      }
    }

    try {
      setLoading(true);

      // Handle file upload if there's a file and it hasn't been uploaded yet (not for seller-farmer)
      let fileUploadResult = uploadedFile; // Use already uploaded file if available
      if (
        userData.role !== "seller-farmer" &&
        formData.uploadIdPhoto &&
        formData.uploadIdPhoto instanceof File &&
        !uploadedFile
      ) {
        console.log("Uploading file during finish step...");
        fileUploadResult = await handleFileUpload(formData.uploadIdPhoto);
      }

      // Prepare final data
      const finalData = {
        ...formData,
        uploadedDocument: fileUploadResult
          ? {
              fileName: fileUploadResult.fileName,
              url: fileUploadResult.url,
            }
          : null,
        submittedAt: new Date().toISOString(),
        role: userData.role,
      };

      // Log all data to console as requested
      console.log("=== PROFILE STEPPER FINAL DATA ===");
      console.log("Full Form Data:", finalData);
      console.log("Step 1 - Profile Info:", {
        fullName: finalData.fullName,
        address: finalData.address,
        country: finalData.country,
        phoneNumber: finalData.phoneNumber,
      });
      if (userData.role !== "seller-farmer") {
        console.log("Step 2 - Products:", {
          productsToSellIds: finalData.productsToSell,
          productsToSellNames: getProductDisplayNames(finalData.productsToSell),
          productsToBuyIds: finalData.productsToBuy,
          productsToBuyNames: getProductDisplayNames(finalData.productsToBuy),
        });
        console.log("Step 3 - KYC:", {
          documentName: finalData.documentName,
          documentIdNumber: finalData.documentIdNumber,
          uploadedDocument: finalData.uploadedDocument,
        });
      }
      console.log("Submission Time:", finalData.submittedAt);
      console.log("================================");

      const body = {
        target: "GPSignup",
        createAMTPLUserInput: { ...finalData },
      };
      const result = await postQuery(body);
      if (result && result.items) {
        console.log("User created successfully:", result.items);
      } else {
        console.log("No user created.");
      }

      // Navigate based on user role
      const roleRoute = userData.role || "global-partner"; // Default to global-partner if role not set
      navigate(`/${roleRoute}`);
    } catch (error) {
      console.error("Error during form submission:", error);
      // You can add error handling UI here if needed
    } finally {
      setLoading(false);
    }
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Clear the upload error when a file is selected
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.uploadedDocument;
        return newErrors;
      });

      try {
        // Start upload immediately when file is selected
        console.log("Starting file upload for:", file.name);
        const uploadResult = await handleFileUpload(file);

        setFormData((prevData) => ({
          ...prevData,
          uploadIdPhoto: file,
        }));

        console.log("File uploaded successfully:", uploadResult);
      } catch (error) {
        console.error("Failed to upload file:", error);
        // Still set the file in case user wants to retry
        setFormData((prevData) => ({
          ...prevData,
          uploadIdPhoto: file,
        }));
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        uploadIdPhoto: null,
      }));
    }
  };

  const handleProductsChange = (name) => (event, selectedProducts) => {
    // Extract product IDs from selected products
    const productIds = selectedProducts
      .map((product) => {
        // Find the product in productList by matching the display string
        const foundProduct = productList.find((p) => {
          const displayName = p.Grade
            ? `${p.ProductName} (${p.Grade})`
            : p.ProductName;
          return displayName === product;
        });
        return foundProduct
          ? foundProduct.ID || foundProduct.ProductID || foundProduct.id
          : null;
      })
      .filter((id) => id !== null); // Remove any null IDs

    setFormData((prevData) => ({
      ...prevData,
      [name]: productIds, // Store IDs instead of names
    }));
  };

  // Helper function to convert product IDs to display names for dropdown value
  const getProductDisplayNames = (productIds) => {
    if (!productIds || !Array.isArray(productIds)) return [];

    return productIds
      .map((id) => {
        const product = productList.find(
          (p) => p.ID === id || p.ProductID === id || p.id === id
        );
        if (product) {
          return product.Grade
            ? `${product.ProductName} (${product.Grade})`
            : product.ProductName;
        }
        return null;
      })
      .filter((name) => name !== null);
  };

  const fieldStyles = {
    backgroundColor: "white",
    borderRadius: "4px",
    "& .MuiOutlinedInput-root": {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "flex-start",
      minHeight: "50px",
      "& fieldset": {
        borderColor: "#ccc",
      },
      "&:hover fieldset": {
        borderColor: "#ff9800",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#fb8c00",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "6px 8px",
      flexGrow: 1,
    },
    "& .MuiChip-root": {
      margin: "2px",
      height: "auto",
    },
  };

  const fileInputStyles = {
    width: "100%",
    maxWidth: "300px",
    "& .upload-button": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#fb8c00",
      color: "#fff",
      borderRadius: "4px",
      padding: "12px 24px",
      height: "50px",
      border: "none",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500",
      transition: "background-color 0.3s ease",
      width: "100%",
      "&:hover": {
        backgroundColor: "#f57c00",
      },
    },
    "& input": {
      display: "none",
    },
  };

  return (
    <div className="kyc-page">
      <div className="section-cs">
        <Box sx={{ width: "100%", padding: 2 }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              "& .MuiStepLabel-label.Mui-active": { color: "#FF9800" },
              "& .MuiStepLabel-label.Mui-completed": { color: "#FF9800" },
              "& .MuiStepIcon-root.Mui-active": { color: "#FF9800" },
              "& .MuiStepIcon-root.Mui-completed": { color: "#FF9800" },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {activeStep === 0 && (
            <Box
              sx={{
                mt: 4,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                minHeight: 300,
                boxShadow: "1px 1px 5px #ccc",
                padding: "20PX",
              }}
            >
              <h2></h2>
              <TextField
                fullWidth
                label="Full Name"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                variant="outlined"
                sx={fieldStyles}
                InputLabelProps={{ shrink: true }}
                placeholder="e.g., John Doe"
                error={!!errors.fullName}
                helperText={errors.fullName}
                required
              />
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                variant="outlined"
                sx={fieldStyles}
                InputLabelProps={{ shrink: true }}
                placeholder="e.g., 123 Main St, City, Country"
                error={!!errors.address}
                helperText={errors.address}
                required
              />
              <TextField
                fullWidth
                select
                label="Country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                variant="outlined"
                sx={fieldStyles}
                InputLabelProps={{ shrink: true }}
                error={!!errors.country}
                helperText={errors.country}
                required
              >
                <MenuItem value="" disabled>
                  Select a country
                </MenuItem>
                {countryMaster.map((c) => (
                  <MenuItem key={c.ID} value={c.Value}>
                    {c.Value}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                variant="outlined"
                sx={fieldStyles}
                InputLabelProps={{ shrink: true }}
                placeholder="e.g., +1 123-456-7890"
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                required
              />
              <div className="section-cs1">
                {userData.role === "seller-farmer" ? (
                  <Button
                    className="submit-btn-more"
                    onClick={handleNextFinish}
                    style={{ backgroundColor: "#FF9800", color: "#fff" }}
                  >
                    Submit
                  </Button>
                ) : (
                  <Button
                    className="submit-btn-more"
                    onClick={handleNext}
                    style={{ backgroundColor: "#FF9800", color: "#fff" }}
                  >
                    Next
                  </Button>
                )}
              </div>
            </Box>
          )}
          {/* Products step - only for global-partner at step 1 */}
          {activeStep === 1 &&
            userData.role !== "buyer" &&
            userData.role !== "seller-farmer" && (
              <Box
                sx={{
                  mt: 4,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  minHeight: 300,
                  boxShadow: "1px 1px 5px #ccc",
                  padding: "20PX",
                }}
              >
                <h2></h2>
                <Autocomplete
                  multiple
                  options={productList.map((product) =>
                    product.Grade
                      ? `${product.ProductName} (${product.Grade})`
                      : product.ProductName
                  )}
                  value={getProductDisplayNames(formData.productsToSell)}
                  onChange={handleProductsChange("productsToSell")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Products to Sell"
                      variant="outlined"
                      sx={fieldStyles}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Select products to sell"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        key={option}
                        label={option}
                        {...getTagProps({ index })}
                        sx={{ margin: "2px", fontSize: "10px" }}
                      />
                    ))
                  }
                />
                <Autocomplete
                  multiple
                  options={productList.map((product) =>
                    product.Grade
                      ? `${product.ProductName} (${product.Grade})`
                      : product.ProductName
                  )}
                  value={getProductDisplayNames(formData.productsToBuy)}
                  onChange={handleProductsChange("productsToBuy")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Products to Buy"
                      variant="outlined"
                      sx={fieldStyles}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Select products to buy"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        sx={{ margin: "2px", fontSize: "10px" }}
                      />
                    ))
                  }
                />
                <div
                  className="section-cs1"
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Button
                    className="submit-btn-more"
                    onClick={handleBack}
                    style={{ backgroundColor: "#FF9800", color: "#fff" }}
                  >
                    Back
                  </Button>
                  <Button
                    className="submit-btn-more"
                    onClick={handleNext}
                    style={{ backgroundColor: "#FF9800", color: "#fff" }}
                  >
                    Next
                  </Button>
                </div>
              </Box>
            )}
          {/* KYC step - for buyer at step 1, for global-partner at step 2, not for seller-farmer */}
          {userData.role !== "seller-farmer" &&
            ((activeStep === 1 && userData.role === "buyer") ||
              (activeStep === 2 && userData.role !== "buyer")) && (
              <Box
                sx={{
                  mt: 4,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  minHeight: 300,
                  boxShadow: "1px 1px 5px #ccc",
                  padding: "20PX",
                }}
              >
                <h2></h2>
                <TextField
                  fullWidth
                  label="Document Name"
                  name="documentName"
                  value={formData.documentName}
                  onChange={handleChange}
                  variant="outlined"
                  sx={fieldStyles}
                  InputLabelProps={{ shrink: true }}
                  placeholder="e.g., Passport"
                  error={!!errors.documentName}
                  helperText={errors.documentName}
                  required
                />
                <TextField
                  fullWidth
                  label="Document ID Number"
                  name="documentIdNumber"
                  value={formData.documentIdNumber}
                  onChange={handleChange}
                  variant="outlined"
                  sx={fieldStyles}
                  InputLabelProps={{ shrink: true }}
                  placeholder="e.g., A12345678"
                  error={!!errors.documentIdNumber}
                  helperText={errors.documentIdNumber}
                  required
                />
                <Box sx={fileInputStyles}>
                  <input
                    id="upload-id-photo"
                    type="file"
                    onChange={handleFileChange}
                    accept="image/*,.pdf"
                  />
                  <label htmlFor="upload-id-photo">
                    <Box
                      className="upload-button"
                      component="span"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#fb8c00",
                        color: "#fff",
                        borderRadius: "4px",
                        padding: "12px 24px",
                        height: "50px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                        transition: "background-color 0.3s ease",
                        width: "100%",
                        "&:hover": {
                          backgroundColor: "#f57c00",
                        },
                      }}
                    >
                      Upload ID Photo
                    </Box>
                  </label>
                </Box>
                {errors.uploadedDocument && (
                  <Box
                    sx={{
                      color: "#d32f2f",
                      fontSize: "0.75rem",
                      marginTop: "-8px",
                      marginLeft: "14px",
                    }}
                  >
                    {errors.uploadedDocument}
                  </Box>
                )}
                {formData.uploadIdPhoto && (
                  <Box sx={{ color: "#555", fontSize: "12px", mt: 1 }}>
                    <span>Selected file: {formData.uploadIdPhoto.name}</span>
                    {uploadedFile && (
                      <Box sx={{ mt: 1, color: "#4caf50", fontWeight: "bold" }}>
                        File uploaded successfully
                      </Box>
                    )}
                  </Box>
                )}
                <div
                  className="section-cs1"
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Button
                    className="submit-btn-more"
                    onClick={handleBack}
                    style={{ backgroundColor: "#FF9800", color: "#fff" }}
                  >
                    Back
                  </Button>
                  <Button
                    className="submit-btn-more"
                    onClick={handleNextFinish}
                    style={{ backgroundColor: "#FF9800", color: "#fff" }}
                  >
                    Submit
                  </Button>
                </div>
              </Box>
            )}
        </Box>
      </div>
    </div>
  );
}
